import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Carrier, CarriersService } from '../../carriers'
import { CurrenciesService, Currency } from '../../currencies'
import { Payment, PaymentsService } from '../../payments'
import { InvoicesService } from '../invoices.service'
import { InvoicesRepository } from '../invoices.repository'
import { Invoice } from '../models/invoice.entity'
import { Order, OrdersService } from '../../orders'
import { User, UsersService } from '../../users'

interface InvoiceDetailState {
  invoice?: Invoice
  carrier?: Carrier
  currencies?: Currency[]
  payment?: Payment
  orders?: Order[]
  users?: User[]
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const GOODS_RETURN_DETAIL_INITIAL_STATE: InvoiceDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class InvoiceDetailStore {
  constructor(
    private invoicesService: InvoicesService,
    private invoicesRepository: InvoicesRepository,
    private carriersService: CarriersService,
    private currenciesService: CurrenciesService,
    private ordersService: OrdersService,
    private paymentsService: PaymentsService,
    private usersService: UsersService,
  ) {}

  private state = signal<InvoiceDetailState>(GOODS_RETURN_DETAIL_INITIAL_STATE)

  // Selectors
  readonly invoice = computed(() => this.state().invoice)
  readonly orders = computed(() => this.state().orders)
  readonly users = computed(() => this.state().users)
  readonly carrier = computed(() => this.state().carrier)
  readonly currencies = computed(() => this.state().currencies)
  readonly payment = computed(() => this.state().payment)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(invoiceId?: string): Promise<void> {
    if (!invoiceId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.invoicesService.read$(invoiceId).subscribe({
        next: async (data) => {
          const extData = await Promise.all([
            this.getCarrier(data.carrierId),
            this.getPayment(data.header.paymentType),
            this.getCurrencies(),
            this.getOrders(data.orderIds),
            this.getUsers([data.createdBy, data.updatedBy]),
          ])
          resolve()
          this.setData(
            data,
            this.state().invoice?._id
              ? this.state().invoice?._id !== data._id
              : false,
            extData,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(invoice: Invoice, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.invoicesService.update$(invoice._id, invoice).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          if (notify === true) {
            this.invoicesRepository.notify({ code: 'INVOICE_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async delete(invoice: Invoice): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.invoicesRepository
        .alert('INVOICE_DELETION', invoice)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.invoicesService.delete$(invoice._id).subscribe({
              next: () => {
                resolve(true)
                this.invoicesRepository.notify({
                  code: 'INVOICE_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async getCarrier(carrierId?: string): Promise<Carrier | undefined> {
    if (!carrierId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.carriersService.read$(carrierId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getPayment(paymentId?: string): Promise<Payment | undefined> {
    if (!paymentId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.paymentsService.read$(paymentId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getCurrencies(): Promise<Currency[] | undefined> {
    return new Promise((resolve) => {
      this.currenciesService.list$().subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getOrders(orderIds?: string[]): Promise<Order[] | undefined> {
    if (!orderIds?.length) {
      return undefined
    }
    return new Promise((resolve) => {
      this.ordersService.search$({ _id: orderIds }).subscribe({
        next: (data) => {
          resolve(data.data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getUsers(userIds?: string[]): Promise<User[] | undefined> {
    if (!userIds?.length) {
      return undefined
    }
    return new Promise((resolve) => {
      this.usersService.search$({ _id: userIds }).subscribe({
        next: (data) => {
          resolve(data.data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(
    invoice: Invoice,
    isModified?: boolean,
    extData?: [
      Carrier | undefined,
      Payment | undefined,
      Currency[] | undefined,
      Order[] | undefined,
      User[] | undefined,
    ],
  ) {
    this.state.set({
      ...this.state(),
      invoice,
      isModified: isModified ?? this.state().isModified,
      carrier: extData?.[0] ?? undefined,
      payment: extData?.[1] ?? undefined,
      currencies: extData?.[2] ?? undefined,
      orders: extData?.[3] ?? undefined,
      users: extData?.[4] ?? undefined,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
